import React, { useState, useRef, useEffect, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import useCustomers from '@/hooks/useCustomers';
import { getCustomerDoc } from '@/services/firebaseApi';
import { ElementPosition } from '@/types';
import { ReactComponent as SearchIcon } from "@/assets/icons/search.svg";
import { ReactComponent as CloseIcon } from "@/assets/icons/close.svg";
import TableList from '@/components/list/tableList/tableList';
import TableListRow from '@/components/list/tableList/tableListRow';
import TableListCell from '@/components/list/tableList/tableListCell';
import Callout from '@/components/callout/callout';
import SearchResultHolder from './SearchResultHolder';
import { ReactComponent as AddIcon } from '@/assets/icons/add.svg';
import useLayout from '@/hooks/useLayout';

interface CustomerSelectorProps {
    storageId?: string;
    className?: string;
    selectedCustomerId?: string;
    onCustomerSelect?: (customerId: string) => void;
    placeholder?: string;
}

const CustomerSelector: FC<CustomerSelectorProps> = ({ 
    storageId,
    className, 
    selectedCustomerId,
    onCustomerSelect,
    placeholder
}) => {
    const { t } = useTranslation();
    const { openDialog } = useLayout();

    const [inFocus, setInFocus] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const CustomerSelectorWrapperRef = useRef<HTMLDivElement>(null);
    const CustomerSelectorResultsRef = useRef<HTMLDivElement>(null);
    const [inputLocation, setInputLocation] = useState<ElementPosition | null>(null);

    const {
        filteredCustomers,
        filterQuery,
        setFilterQuery,
        customers
    } = useCustomers({ storageId });

    // Get selected customer for display
    const selectedFromList = selectedCustomerId 
        ? customers?.find(customer => customer.id === selectedCustomerId) 
        : undefined;
    const { data: selectedFromDoc } = useQuery({
        queryKey: ['customer', storageId, selectedCustomerId],
        queryFn: () => getCustomerDoc(storageId!, selectedCustomerId!),
        enabled: !!(storageId && selectedCustomerId) && !selectedFromList,
        staleTime: 5 * 60 * 1000,
    });
    const selectedCustomer = selectedFromList || selectedFromDoc;

    const handleOnClick = () => {
        setInFocus(true);
        if (CustomerSelectorWrapperRef.current) {
            const rect = CustomerSelectorWrapperRef.current.getBoundingClientRect();
            setInputLocation({
                top: rect.bottom,
                left: rect.left,
                width: rect.width
            });
        }
    };

    const handleOnBlur = () => {
        setInFocus(false);
    };

    const handleSearchIconClick = () => {
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };

    const location = useLocation();

    useEffect(() => {
        handleOnBlur();
        // eslint-disable-next-line
    }, [location]);

    // Update rect on window resize if inFocus
    useEffect(() => {
        if (!inFocus) return;
        const updateRect = () => {
            if (CustomerSelectorWrapperRef.current) {
                const rect = CustomerSelectorWrapperRef.current.getBoundingClientRect();
                setInputLocation({
                    top: rect.bottom,
                    left: rect.left,
                    width: rect.width
                });
            }
        };
        window.addEventListener('resize', updateRect);
        updateRect();
        return () => window.removeEventListener('resize', updateRect);
    }, [inFocus]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (
                CustomerSelectorWrapperRef.current && !CustomerSelectorWrapperRef.current.contains(target) &&
                CustomerSelectorResultsRef.current && !CustomerSelectorResultsRef.current.contains(target)
            ) {
                handleOnBlur();
            }
        };

        if (inFocus) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [inFocus]);

    const handleCreateCustomer = () => {
        if (!storageId) return;
        handleOnBlur();
        openDialog('create_customer', {
            storageId,
            onCreated: (createdId: string) => {
                onCustomerSelect && onCustomerSelect(createdId);
            }
        });
    };

    const createButton = storageId ? (
        <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center p-0.5 rounded hover:bg-gray-100"
            onClick={handleCreateCustomer}
            aria-label={t('buttonCreateCustomer')}
        >
            <AddIcon className="h-5 w-5 fill-gray-500" />
        </button>
    ) : null;

    const selectedItemPreview = selectedCustomerId ? (
        <div className="relative p-3 pr-10 bg-gray-50 border border-gray-300 rounded-md flex items-center justify-between">
            <div>
                <div className="font-medium">{selectedCustomer ? `${selectedCustomer.customerId}: ${selectedCustomer.name}` : '...'}</div>
                {selectedCustomer?.contactInformation?.email && (
                    <div className="text-sm text-gray-600">{selectedCustomer.contactInformation.email}</div>
                )}
                {selectedCustomer?.contactInformation?.phone && (
                    <div className="text-sm text-gray-600">{selectedCustomer.contactInformation.phone}</div>
                )}
            </div>
            <CloseIcon 
                className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer fill-gray-500 hover:fill-black"
                onClick={() => onCustomerSelect && onCustomerSelect('')}
            />
        </div>
    ) : null;

    const customerSelector = selectedCustomerId ? null : (
        <div ref={CustomerSelectorWrapperRef} className={`relative ${className ? className : ''}`}>
            <div className="relative w-full">
                <SearchIcon 
                    className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${storageId ? 'cursor-pointer' : 'cursor-not-allowed'} fill-gray-500`}
                    onClick={handleSearchIconClick}
                />
                <input 
                    type="text"
                    placeholder={inFocus ? (placeholder || t('placeholderSearchCustomer', 'Search customers...')) : ''}
                    className={`
                        rounded-md
                        w-full
                        focus:outline-none
                        pl-10
                        ${storageId ? (inFocus && filterQuery.length > 0 ? 'pr-16' : 'pr-10') : 'pr-10'}
                        ${inFocus ? 'border-gray-500 border-b-white rounded-b-none' : ''}
                    `} 
                    value={inFocus ? filterQuery : ''}
                    onChange={(e) => setFilterQuery(e.target.value)}
                    data-ignore="true"
                    onFocus={handleOnClick}
                    ref={searchInputRef}
                    disabled={!storageId}
                />
                {inFocus && filterQuery.length > 0 && (
                    <CloseIcon 
                        className={`absolute top-1/2 transform -translate-y-1/2 cursor-pointer fill-black ${storageId ? 'right-9' : 'right-3'}`} 
                        onClick={() => setFilterQuery('')}
                    />
                )}
                {createButton}
            </div>
        </div>
    );

    const results = selectedCustomerId ? null : inFocus && inputLocation && (
        <SearchResultHolder
            inputRef={inputLocation}
            resultsRef={CustomerSelectorResultsRef}
        >
            {filterQuery.length > 0 && !filteredCustomers.length ? (
                <Callout type="error">{t('noResultsFound')}</Callout>
            ) : null}

            {filteredCustomers.length > 0 ? (
                <TableList className="small-list hover-effect border-none rounded-t-none">
                    <TableListRow isHeader>
                        <TableListCell>{t('titleId', 'ID')}</TableListCell>
                        <TableListCell>{t('titleName', 'Name')}</TableListCell>
                        <TableListCell>{t('titleEmail', 'Email')}</TableListCell>
                        <TableListCell>{t('titlePhone', 'Phone')}</TableListCell>
                    </TableListRow>
                    {filteredCustomers.map((customer) => (
                        <TableListRow
                            key={customer.id}
                            onClick={() => {
                                onCustomerSelect && onCustomerSelect(customer.id as string);
                                handleOnBlur();
                            }}
                            className="cursor-pointer"
                        >
                            <TableListCell>{customer.customerId}</TableListCell>
                            <TableListCell>{customer.name}</TableListCell>
                            <TableListCell>{customer.contactInformation?.email || '-'}</TableListCell>
                            <TableListCell>{customer.contactInformation?.phone || '-'}</TableListCell>
                        </TableListRow>
                    ))}
                </TableList>
            ) : null}
        </SearchResultHolder>
    );

    return (
        <>
            {customerSelector}
            {results}
            {selectedItemPreview}
        </>
    );
};

export default CustomerSelector;
